'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, type Variants } from 'framer-motion';
import { Send, MessageSquare, CheckCircle2, AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { FaInstagram, FaTelegram, FaYoutube } from "react-icons/fa";
import { Skeleton } from "@/components/ui/Skeleton";
import { BackButton } from '@/components/ui/BackButton';
import { useLocale } from '@/i18n/context';

export default function ContactUsPage() {
  const router = useRouter();
  const { t, isRtl } = useLocale();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const maxChars = 500;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [formData.message]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > maxChars) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setStatus('submitting');

    try {
      const response = await fetch("https://formsubmit.co/ajax/saeedreza.tyb.88@gmail.com", {
        method: "POST",
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: "پیام جدید از فرم تماس کمپین سبز",
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _template: "table"
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error("خطا در پاسخ سرور");
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }
  };

  const socialLinks = [
    { name: 'Instagram', icon: FaInstagram, url: 'https://www.instagram.com/campaign_sabz?igsh=MWgycmttNHhqMG0zdA==', color: 'hover:text-pink-600 dark:hover:text-pink-400 hover:bg-pink-50 dark:hover:bg-zinc-800/80 hover:border-pink-200 dark:hover:border-pink-500/30' },
    { name: 'Telegram', icon: FaTelegram, url: 'https://t.me/campaign_sabz', color: 'hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-zinc-800/80 hover:border-blue-200 dark:hover:border-blue-500/30' },
    { name: 'YouTube', icon: FaYoutube, url: 'https://youtube.com/@campaignsabz?si=e7DSa7_Aie20uaUr', color: 'hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-zinc-800/80 hover:border-red-200 dark:hover:border-red-500/30' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-zinc-950 pt-8 sm:pt-12 md:pt-20 pb-16 px-4 sm:px-6 lg:px-8 text-[#206A4F] dark:text-emerald-400 relative overflow-hidden transition-colors duration-300" dir={isRtl ? "rtl" : "ltr"}>
      <div className="max-w-6xl mx-auto relative">
        
        {/* Back Button */}
        <div className="flex justify-start mb-6 md:mb-10">
          <BackButton href="/" label={t.common.back} />
        </div>

        {/* Header Section */}
        <div className="text-center mb-10 md:mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-14 h-14 sm:w-16 sm:h-16 bg-[#206A4F]/10 dark:bg-emerald-500/15 text-[#206A4F] dark:text-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm border border-[#206A4F]/10 dark:border-emerald-500/30"
          >
            <MessageSquare className="w-7 h-7 sm:w-8 sm:h-8 animate-pulse" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-3 sm:mb-4 text-[#206A4F] dark:text-white"
          >
            {t.contactUs.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-[#206A4F]/80 dark:text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed font-medium"
          >
            {t.contactUs.subtitle}
          </motion.p>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-5 gap-8"
        >
          {/* Social Links */}
          <motion.div variants={itemVariants} className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-[#F0F7F3] dark:bg-zinc-900/80 rounded-3xl p-6 sm:p-8 border border-[#206A4F]/10 dark:border-zinc-800 h-full flex flex-col shadow-sm transition-colors duration-300">
              <h3 className="text-xl sm:text-2xl font-black mb-6 text-[#206A4F] dark:text-white">{t.contactUs.socialTitle}</h3>
              <p className="text-[#206A4F]/70 dark:text-zinc-400 mb-6 leading-relaxed text-sm font-medium">
                {t.contactUs.subtitle}
              </p>
              
              <div className="flex flex-col gap-4 mt-auto">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, x: isRtl ? 5 : -5 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center gap-4 p-4 bg-white dark:bg-zinc-900 border border-[#206A4F]/10 dark:border-zinc-800 rounded-2xl transition-all shadow-sm group ${social.color}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#F5F0E8] dark:bg-zinc-800 flex items-center justify-center text-[#206A4F] dark:text-emerald-400 group-hover:bg-white dark:group-hover:bg-zinc-700 transition-colors shrink-0 shadow-inner">
                      <social.icon size={20} />
                    </div>
                    <span className="font-extrabold text-[#206A4F] dark:text-white group-hover:text-inherit transition-colors">
                      {social.name}
                    </span>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <div className="bg-white dark:bg-zinc-900/90 rounded-3xl p-6 sm:p-8 border border-[#206A4F]/10 dark:border-zinc-800 shadow-xl shadow-[#206A4F]/5 dark:shadow-none transition-colors duration-300">
              <h3 className="text-xl sm:text-2xl font-black mb-6 text-[#206A4F] dark:text-white">{t.contactUs.formTitle}</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2 text-[#206A4F] dark:text-zinc-300">{t.contactUs.nameLabel}</label>
                    <input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t.contactUs.namePlaceholder}
                      className="w-full bg-[#F5F0E8]/60 dark:bg-zinc-800/80 border border-[#206A4F]/15 dark:border-zinc-700 rounded-2xl px-4 py-3.5 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#206A4F]/40 dark:focus:ring-emerald-500/40 transition-all placeholder:text-[#206A4F]/40 dark:placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-[#206A4F] dark:text-zinc-300">{t.contactUs.emailLabel}</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t.contactUs.emailPlaceholder}
                      className="w-full bg-[#F5F0E8]/60 dark:bg-zinc-800/80 border border-[#206A4F]/15 dark:border-zinc-700 rounded-2xl px-4 py-3.5 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#206A4F]/40 dark:focus:ring-emerald-500/40 transition-all placeholder:text-[#206A4F]/40 dark:placeholder:text-zinc-500 dir-ltr text-left"
                    />
                  </div>
                </div>

                <div className="relative">
                  <div className="flex justify-between items-end mb-2 px-1">
                    <label className="block text-sm font-bold text-[#206A4F] dark:text-zinc-300">{t.contactUs.msgLabel}</label>
                    <span className={`text-xs font-black transition-colors ${
                      formData.message.length > maxChars * 0.9 ? 'text-red-500 dark:text-red-400' : 'text-[#206A4F]/50 dark:text-zinc-500'
                    }`}>
                      {formData.message.length} / {maxChars}
                    </span>
                  </div>
                  <textarea 
                    ref={textareaRef}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder={t.contactUs.msgPlaceholder}
                    className="w-full bg-[#F5F0E8]/60 dark:bg-zinc-800/80 border border-[#206A4F]/15 dark:border-zinc-700 rounded-2xl px-4 py-3.5 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#206A4F]/40 dark:focus:ring-emerald-500/40 transition-all placeholder:text-[#206A4F]/40 dark:placeholder:text-zinc-500 overflow-hidden resize-none"
                  />
                </div>

                {status === 'success' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 p-4 rounded-xl border border-emerald-200 dark:border-emerald-500/30">
                    <CheckCircle2 size={20} className="text-emerald-500 shrink-0" />
                    <span className="font-bold text-sm">{t.contactUs.successMsg}</span>
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50 p-4 rounded-xl border border-red-200 dark:border-red-500/30">
                    <AlertCircle size={20} className="text-red-500 shrink-0" />
                    <span className="font-bold text-sm">{t.contactUs.errorMsg}</span>
                  </motion.div>
                )}

                <button 
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full sm:w-auto flex items-center justify-center gap-2.5 bg-[#206A4F] dark:bg-emerald-500 hover:bg-[#18533E] dark:hover:bg-emerald-400 text-[#F5F0E8] dark:text-black px-8 py-4 rounded-2xl font-black shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-200 disabled:opacity-70 disabled:hover:translate-y-0 cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <Skeleton className="w-5 h-5 rounded-full bg-white/20" />
                  ) : (
                    <>
                      <Send size={18} className="rtl:-scale-x-100" />
                      <span>{t.contactUs.sendBtn}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </motion.div>

      </div>
    </div>
  );
}